import _ from "lodash";
import { getAssessmentFromLocalStorage } from "Assessments/modules/Utils";
import moment from "moment";
import { getFormattedCoursesDetailsMemoize } from "ClassRoom/modules/utils";
import { getDateTimeStringsBasedOnFormat } from "Utils";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import { getIsWorksheetAssignModalV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  ASSESSMENT_CREATION,
  ASSESSMENT_DETAILS,
  FMT_CREATION,
  LE_CREATION,
  AI_TUTOR_CREATION,
  QT_CREATION,
  QUESTION_CREATION,
  SMT_CREATION,
  WORKSHEET_QUESTION_CREATION,
  OPENS_ON,
  DUE_ON,
  CLOSES_ON,
  VISIBILITY,
  VISIBLE,
  ASSESSMENT_TYPE_AI_TUTOR,
} from "Constants/stringConstants";
import {
  checkIsDateBefore,
  checkIsDateSame,
  formatDate,
} from "Utils/DateTimeHelpers";
import { getAssignedStudentsAIConversation } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import {
  getDueOnBasedOnTimelineSettings,
  getMomentFromDateAndTimeString,
  getOpensOnBasedOnTimelineSettings,
} from "ClassRoom/components/AddAssignment/AssignmentSettings/Utils";
import { getSettingValue } from "modules/PermissionModule";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

/**
 * Generates a date-time string from the provided date and time strings.
 *
 * @param {Object} params - The parameters for generating the date-time string.
 * @param {string} params.dateString - The date string.
 * @param {string} params.timeString - The time string.
 * @returns {string|null} The generated date-time string, or null if no date string is provided.
 */

export const generateDateTimeString = ({ dateString, timeString }) => {
  if (dateString && timeString) {
    return `${dateString}:${timeString}`;
  } else if (dateString) {
    return `${dateString}`;
  } else {
    return null;
  }
};

/**
 * Gets unique generic folders from the provided local folder names and generic folders of a course.
 *
 * @param {Object} params - The parameters for getting unique generic folders.
 * @param {Array} params.folderNamesLocal - The local folder names.
 * @param {Array} params.genericFolderOfCourse - The generic folders of a course.
 * @returns {Array} The unique generic folders.
 */

export const getUniqGenericFolders = params => {
  const { folderNamesLocal, genericFolderOfCourse } = params;

  const uniqGenericFolders = _.reduce(
    genericFolderOfCourse,
    (result, genericFolder) => {
      const folderIndex = _.findIndex(
        result,
        obj => obj.label == genericFolder.label
      );

      if (folderIndex < 0) {
        result.push({
          label: genericFolder.label,
          value: genericFolder.value,
          color: genericFolder.color,
        });
      }

      return result;
    },
    folderNamesLocal
  );

  return uniqGenericFolders;
};

const getDefaultSettings = ({
  assignmentDefaultSettings,
  creationSource,
  assessmentType,
}) => {
  const {
    isClassDiscussionEnabled = false,
    isStudentSubmissionEnabled = true,
    isExcludedFromFinalGradeCalculation = false,
    isSharedWithClassMaterial = false,
    isIncludedInCourseFlow = getIsLearningCoursesEnabled(),
  } = assignmentDefaultSettings || {};

  return {
    isClassDiscussionEnabled,
    isStudentSubmissionEnabled:
      _.includes(
        [ASSESSMENT_TYPE_WORKSHEET, ASSESSMENT_TYPE_AI_TUTOR],
        assessmentType
      ) || isStudentSubmissionEnabled,
    isExcludedFromFinalGradeCalculation,
    isSharedWithClassMaterial:
      creationSource === "CLASS_MATERIALS" || isSharedWithClassMaterial,
    isIncludedInCourseFlow:
      creationSource === "COURSE_FLOW" || isIncludedInCourseFlow,
  };
};

export const getInitialSelectedState = params => {
  const {
    mode,
    resourceSelection,
    clonedAssessmentId,
    courseId,
    creationMode,
    assignmentData,
    contentType,
    sourceAssessmentId,
    isSimilarityCheckEnabledDefaultValue,
    selectedTimeSlot,
    showCalendarAssignmentByDueDate,
    course,
    preSelectedCoursesDetails,
    isDuplicateAssignment,
    isSettingsStepOnly,
    creationSource,
    assessmentType,
    userId,
    currentCurriculumProgramId,
    filteredStaffCourses,
    assignmentDefaultSettings,
    sendAssignNotificationPreference,
  } = params;

  if (resourceSelection) {
    return {
      unitPlanId: "",
      resource: { label: "", attachments: [] },
    };
  }

  if (mode === "create") {
    let assignmentData = {};

    try {
      assignmentData = JSON.parse(
        getAssessmentFromLocalStorage({
          courseId,
          creationMode,
        })
      );

      assignmentData = _.get(assignmentData, "data", {});
    } catch (e) {
      console.error(e);
    }

    if (!_.isEmpty(assignmentData) && !isSettingsStepOnly) {
      return assignmentData;
    }

    /**
     * In new course setup when assessment created from course flow then...
       select all staff courses(classes) of that learning course by default
     * else if created from assignments module, gradebook then select only current course(class)
     */
    //TODO: refactor condition when integrate flow in toddle/school library, calendar
    const coursesInitialState = !isDuplicateAssignment
      ? creationSource === "COURSE_FLOW" && !_.isEmpty(filteredStaffCourses)
        ? getFormattedCoursesDetailsMemoize({
            courses: filteredStaffCourses,
            selectedTimeSlot,
            itemType: "ASSIGNMENT",
            showCalendarAssignmentByDueDate,
          })
        : !_.isEmpty(courseId)
        ? getFormattedCoursesDetailsMemoize({
            courses: [course],
            selectedTimeSlot,
            itemType: "ASSIGNMENT",
            showCalendarAssignmentByDueDate,
          })
        : getFormattedCoursesDetailsMemoize({
            courses: preSelectedCoursesDetails,
            selectedTimeSlot,
            itemType: "ASSIGNMENT",
            showCalendarAssignmentByDueDate,
          })
      : [];
    const showStudentResponse = getSettingValue({
      name: "ResponseVisibilityConfiguration",
      userId,
      currentCurriculumProgramId,
    });

    return {
      unitPlanId: "",
      unitPlanTemplateId: "",
      leId: clonedAssessmentId ?? "",
      importedLeId: sourceAssessmentId || "",
      creationMode,
      selectedCourse: {},
      isTeacherEvaluationEnabled: false,
      isStudentEvaluationEnabled: false,
      isSimilarityCheckEnabled: isSimilarityCheckEnabledDefaultValue,
      isOnScreenCalculatorEnabled: false,
      showStudentResponse,
      isPrivate: false,
      folderNames: [],
      courses: coursesInitialState,
      meeting: {
        url: "",
        type: "ZOOM",
        authToken: "",
        microsoftAuthToken: "",
        isZoomAuthorized: false,
        scheduleDate: selectedTimeSlot
          ? _.get(selectedTimeSlot, "startDate", null)
          : moment().add(1, "days").format("YYYY-MM-DD"),
        scheduleTime: selectedTimeSlot
          ? _.get(selectedTimeSlot, "startTime", null)
          : "00:00",
      },
      contentType,
      resource: {
        label: "",
        attachments: [],
        title: "",
      },
      isTimedTestEnabled: false,
      isFixedStartTime: false,
      isLockScreenEnabled: false,
      isSafeBrowserEnabled: false,
      timedTestDuration: {
        hours: 1,
        minutes: 0,
      },
      isLockSubmissionEnabled: false,
      ...getDefaultSettings({
        assignmentDefaultSettings,
        creationSource,
        assessmentType,
      }),
      isSendAssignedNotification: sendAssignNotificationPreference ?? true,
    };
  } else {
    return assignmentData ?? { leId: clonedAssessmentId };
  }
};

export const getStepsInfo = params => {
  const { isFromUnitPlan, isWorksheet } = params;
  let { steps } = params;

  let isSettingsStepOnly = false;

  const hasSettingsStep = _.some(steps, { value: "SETTINGS" });

  if (hasSettingsStep) {
    if (isFromUnitPlan && !isWorksheet && _.size(steps) > 1) {
      steps = _.filter(steps, ({ value }) => value === "SETTINGS");
      isSettingsStepOnly = true;
    } else if (_.size(steps) > 1) {
      steps = _.filter(steps, ({ value }) => value !== "SETTINGS");
    } else if (_.size(steps) === 1) {
      isSettingsStepOnly = true;
    }
  }

  return {
    steps,
    isSettingsStepOnly,
  };
};

export const getPreselectedCourseIds = params => {
  const { steps } = params;

  let preSelectedCourseIds = _.get(params, "preSelectedCourseIds", null);
  const courseList = _.get(params, "courseList", null, steps);

  //fetch all courses details when unit from course selction
  if (_.includes(_.map(steps, "value"), "UNIT_FROM_GRADE_SELECTION")) {
    preSelectedCourseIds = _.map(courseList, item => item.courseId);
  }

  return _.compact(preSelectedCourseIds);
};

export const getCourseAcademicYearsIds = params => {
  const { course } = params;
  return _.map(_.get(course, "academicYears", []), item => item.id);
};

export const getFormattedCloseDateTimeString = closeSubmissionDate => {
  if (_.isEmpty(closeSubmissionDate)) {
    return null;
  }
  return `${formatDate({
    date: closeSubmissionDate,
    format: "YYYY-MM-DD",
  })}:${formatDate({
    date: closeSubmissionDate,
    format: "HH:mm",
  })}`;
};

const getLinkedAssignmentsEditInitialData = (
  linkedAssignments,
  {
    isLockScreenEnabled = false,
    isSafeBrowserEnabled = false,
    timedTestDuration = { hours: 1, minutes: 0 },
  } = {}
) => {
  return _.map(linkedAssignments, assignment => {
    const {
      course: { id, title } = {},
      deadline,
      assignedStudents = [],
      folders = [],
      state: { state, publishedAt } = {},
      closeSubmissionDate,
      visibility,
    } = assignment;

    const students = _.map(assignedStudents, ({ student }) => student);
    const totalStudents = _.map(
      _.get(assignment, "course.students.edges", []),
      ({ node }) => node
    );
    const {
      dateString: deadlineDate,
      timeString: deadlineTime,
    } = getDateTimeStringsBasedOnFormat({
      inputDateTimeString: deadline,
    });
    const folderNames = _.map(folders, ({ id }) => id);

    return {
      id,
      title,
      students,
      totalStudents,
      deadlineDate,
      deadlineTime,
      folderNames,
      state: {
        state,
        scheduleDate: publishedAt
          ? moment(publishedAt).format("YYYY-MM-DD")
          : null,
        scheduleTime: publishedAt ? moment(publishedAt).format("HH:mm") : null,
      },
      closeSubmissionDate: getFormattedCloseDateTimeString(closeSubmissionDate),
      visibility,
      ...(getIsWorksheetAssignModalV2Enabled()
        ? { isLockScreenEnabled, isSafeBrowserEnabled, timedTestDuration }
        : {}),
    };
  });
};

export const getEditAssignmentInitialCoursesState = params => {
  const {
    course = {},
    courseId,
    assignedStudents,
    deadline,
    state: { state, publishedAt } = {},
    folders,
    groupedAssignments = [],
    closeSubmissionDate,
    visibility,
    linkedAssignments = [],
    isLockScreenEnabled = false,
    isSafeBrowserEnabled = false,
    timedTestDuration = { hours: 1, minutes: 0 },
  } = params;

  /**
   * In new course setup there will be only single assessment and multiple class assignments linked to it.
   * So at time of edit assessment/assignment show all linked assignments selected by default in assign modal....
   * irrespective of the place where we are editing the assignment (course flow / unit flow / class assignments etc.)
   */
  if (!_.isEmpty(linkedAssignments) && getIsLearningCoursesEnabled()) {
    let sortedLinkedAssignments = linkedAssignments;
    if (!_.isEmpty(courseId)) {
      // If assignment edit from inside class then select that class as first
      sortedLinkedAssignments = _.sortBy(linkedAssignments, assignment => {
        const { course: { id } = {} } = assignment;
        return id === courseId ? -1 : 1;
      });
    }
    return getLinkedAssignmentsEditInitialData(sortedLinkedAssignments, {
      isLockScreenEnabled,
      isSafeBrowserEnabled,
      timedTestDuration,
    });
  }

  const students = _.map(assignedStudents, ({ student }) => student);

  const totalStudents = _.map(
    _.get(course, "students.edges", []),
    ({ node }) => node
  );

  const {
    dateString: deadlineDate,
    timeString: deadlineTime,
  } = getDateTimeStringsBasedOnFormat({
    inputDateTimeString: deadline,
  });

  const folderNames = _.map(folders, ({ id }) => id);

  const courses = [
    {
      id: courseId,
      title: _.get(course, "title", ""),
      students,
      totalStudents,
      deadlineDate,
      deadlineTime,
      folderNames,
      state: {
        state,
        scheduleDate: publishedAt
          ? moment(publishedAt).format("YYYY-MM-DD")
          : null,
        scheduleTime: publishedAt ? moment(publishedAt).format("HH:mm") : null,
      },
      closeSubmissionDate: getFormattedCloseDateTimeString(closeSubmissionDate),
      visibility,
      ...(getIsWorksheetAssignModalV2Enabled()
        ? { isLockScreenEnabled, isSafeBrowserEnabled, timedTestDuration }
        : {}),
    },
  ];

  // draft assignment edit mode case
  if (state === "DRAFT" && !_.isEmpty(groupedAssignments)) {
    _.forEach(groupedAssignments, otherClassAssignment => {
      const {
        course: { id, title } = {},
        deadline,
        assignedStudents = [],
        folders = [],
        state: { state, publishedAt } = {},
        closeSubmissionDate,
        visibility,
      } = otherClassAssignment;

      const students = _.map(assignedStudents, ({ student }) => student);
      const totalStudents = _.map(
        _.get(course, "students.edges", []),
        ({ node }) => node
      );
      const {
        dateString: deadlineDate,
        timeString: deadlineTime,
      } = getDateTimeStringsBasedOnFormat({
        inputDateTimeString: deadline,
      });
      const folderNames = _.map(folders, ({ id }) => id);

      courses.push({
        id,
        title,
        students,
        totalStudents,
        deadlineDate,
        deadlineTime,
        folderNames,
        state: {
          state,
          scheduleDate: publishedAt
            ? moment(publishedAt).format("YYYY-MM-DD")
            : null,
          scheduleTime: publishedAt
            ? moment(publishedAt).format("HH:mm")
            : null,
        },
        closeSubmissionDate: getFormattedCloseDateTimeString(
          closeSubmissionDate
        ),
        visibility,
        ...(getIsWorksheetAssignModalV2Enabled()
          ? { isLockScreenEnabled, isSafeBrowserEnabled, timedTestDuration }
          : {}),
      });
    });
  }

  return courses;
};

export const getSteps = params => {
  const {
    t,
    assessmentType,
    headerSteps,
    isSettingsStepOnly,
    isWorksheetCreateEditMode,
  } = params;

  // * removing `QUESTION_CREATION (worksheet)` step as we load worksheet from AssessmentDetails > ResourceEdit > WithMiddlePanelHOC
  if (isWorksheetCreateEditMode) {
    return _.filter(
      headerSteps,
      step => !_.isEqual(QUESTION_CREATION, _.get(step, "value"))
    );
  }

  return assessmentType === ASSESSMENT_TYPE_WORKSHEET &&
    !_.some(headerSteps, { value: QUESTION_CREATION }) &&
    !isSettingsStepOnly
    ? [
        ...headerSteps,
        {
          label: t("worksheet:questions"),
          value: QUESTION_CREATION,
          heading: t("worksheet:questions"),
          subHeading: t("classRoom:classes_discussion_shared_with"),
        },
      ]
    : headerSteps;
};

export const getGenericFolders = params => {
  const { mode, courses, folderNames, genericFolders } = params || {};

  if (mode == "edit") return genericFolders;

  const genericFolderOfCourse = _.filter(genericFolders, genericFolder => {
    const index = _.findIndex(
      courses,
      course => course.id == genericFolder.courseId
    );
    return index > -1;
  });

  const folderNamesLocal = _.map(folderNames, item => {
    const labelOfLocalFolder = _.get(
      _.find(genericFolders, genericFolder => genericFolder.value == item),
      "label",
      item
    );
    return {
      label: labelOfLocalFolder,
      value: item,
    };
  });

  return getUniqGenericFolders({
    folderNamesLocal,
    genericFolderOfCourse,
  });
};

export const getAddedCoursesData = ({
  addedCourses,
  metadata = {},
  dueTimePreference,
  isFixedStartTime = false,
  timedTestDuration = {},
  backDateCourses = [],
}) => {
  let isScheduled = false;
  const courses = _.map(addedCourses, course => {
    const {
      id,
      title,
      state: { scheduleDate, scheduleTime } = {},
      deadlineDate,
      deadlineTime,
      students,
      folderNames,
      closeSubmissionDate,
      visibility,
      // Per-class worksheet settings (when WorksheetAssignModalV2 is enabled)
      isLockScreenEnabled: courseIsLockScreenEnabled,
      isSafeBrowserEnabled: courseIsSafeBrowserEnabled,
      timedTestDuration: courseTimedTestDuration,
    } = course;

    const assignmentLevelPreferences = _.get(metadata, "preferences", []);
    const deadlineString = generateDateTimeString({
      dateString: deadlineDate,
      timeString: deadlineTime,
    });
    const isDueDateInPast = checkIsDateBefore({
      date1: moment(deadlineString),
      date2: moment(),
      key: "minutes",
    });

    const scheduleString = getScheduleString({
      scheduleDate,
      scheduleTime,
      assignmentLevelPreferences,
      isDueDateInPast,
    });

    const scheduleStringToSend = moment(
      scheduleString,
      "YYYY-MM-DD:HH:mm",
      true
    ).isValid()
      ? moment(scheduleString, "YYYY-MM-DD:HH:mm").toISOString()
      : moment(scheduleString, "YYYY-MM-DD").toISOString();

    const dueTime = !_.isEmpty(dueTimePreference) ? dueTimePreference : "09:00";

    const deadlineStringToSend = !_.isEmpty(deadlineString)
      ? moment(deadlineString, "YYYY-MM-DD:HH:mm", true).isValid()
        ? moment(deadlineString, "YYYY-MM-DD:HH:mm").toISOString()
        : moment(
            `${deadlineString}:${dueTime}`,
            "YYYY-MM-DD:HH:mm"
          ).toISOString()
      : null;

    if (
      !_.isEmpty(scheduleDate) &&
      moment(scheduleStringToSend).isAfter(moment())
    ) {
      isScheduled = true;
    }

    const newCourseData = {
      courseId: id,
      deadline: deadlineStringToSend,
      publishedAt: scheduleStringToSend,
      students: _.compact(
        _.uniq(_.map(students, student => _.get(student, "id")))
      ),
      folderNames,
      title,
    };

    newCourseData.isBackdateAssignment = _.get(
      _.find(backDateCourses, { id }),
      "isBackdateAssignment",
      false
    );

    newCourseData.visibility = _.includes(
      assignmentLevelPreferences,
      VISIBILITY
    )
      ? visibility
      : VISIBLE;

    const closeDateStringToSend =
      !_.isEmpty(closeSubmissionDate) &&
      _.includes(assignmentLevelPreferences, CLOSES_ON)
        ? moment(closeSubmissionDate, "YYYY-MM-DD:HH:mm").toISOString()
        : null;
    newCourseData.closeSubmissionDate = isFixedStartTime
      ? moment(scheduleStringToSend)
          .add(timedTestDuration.hours, "hours")
          .add(timedTestDuration.minutes, "minutes")
          .toISOString()
      : closeDateStringToSend;
    newCourseData.deadline = _.includes(assignmentLevelPreferences, DUE_ON)
      ? isFixedStartTime
        ? moment(scheduleStringToSend)
            .add(timedTestDuration.hours, "hours")
            .add(timedTestDuration.minutes, "minutes")
            .toISOString()
        : deadlineStringToSend
      : null;

    // When WorksheetAssignModalV2 is enabled, include per-class security mode
    // and duration in the course payload instead of sending them as global settings
    if (getIsWorksheetAssignModalV2Enabled()) {
      newCourseData.isLockScreenEnabled = courseIsLockScreenEnabled || false;
      newCourseData.isSafeBrowserEnabled = courseIsSafeBrowserEnabled || false;
      if (courseTimedTestDuration) {
        newCourseData.timedTestDuration =
          courseTimedTestDuration.hours * 60 + courseTimedTestDuration.minutes;
      }
    }

    return newCourseData;
  });

  return { courses, isScheduled };
};

// CASE: when assignment mode is "edit"
// returns updated course data based on old course data and new course data
export const getUpdatedCourseData = params => {
  const {
    oldData,
    newData,
    genericFoldersLocal,
    metadata = {},
    dueTimePreference,
    isFixedStartTime = false,
    timedTestDuration = {},
  } = params;

  const assignmentLevelPreferences = _.get(metadata, "preferences", []);
  const updatedData = {};

  const oldFolderNames = _.get(oldData, "folderNames", []);
  const newFolderNames = _.get(newData, "folderNames", []);

  if (!_.isEqual(newFolderNames, oldFolderNames)) {
    const addedFolders = _.filter(newFolderNames, id => {
      return (
        _.findIndex(oldFolderNames, oldFolderName => oldFolderName == id) < 0
      );
    });
    const addedFolderNames = _.map(addedFolders, addedFolder => {
      return _.get(
        _.find(
          genericFoldersLocal,
          genericFolder => genericFolder.value == addedFolder
        ),
        "label",
        addedFolder
      );
    });
    const removedFolderIds = _.filter(oldFolderNames, id => {
      return (
        _.findIndex(newFolderNames, newFolderName => newFolderName == id) < 0
      );
    });
    updatedData["folders"] = { addedFolderNames, removedFolderIds };
  }

  const oldStudents = _.get(oldData, "students", []);
  const newStudents = _.get(newData, "students", []);
  const addedStudents = _.map(
    _.differenceBy(newStudents, oldStudents, "id"),
    student => student.id
  );
  const removedStudents = _.map(
    _.differenceBy(oldStudents, newStudents, "id"),
    student => student.id
  );
  updatedData["students"] = {
    addedStudents,
    removedStudents,
  };

  const deadlineDate = _.get(newData, "deadlineDate", null);
  const deadlineTime = _.get(newData, "deadlineTime", null);
  const deadlineString = generateDateTimeString({
    dateString: deadlineDate,
    timeString: deadlineTime,
  });
  const isDueDateInPast = checkIsDateBefore({
    date1: moment(deadlineString),
    date2: moment(),
    key: "minutes",
  });
  const dueTime = !_.isEmpty(dueTimePreference) ? dueTimePreference : "09:00";
  const deadlineStringToSend = !_.isEmpty(deadlineString)
    ? moment(deadlineString, "YYYY-MM-DD:HH:mm", true).isValid()
      ? moment(deadlineString, "YYYY-MM-DD:HH:mm").toISOString()
      : moment(`${deadlineString}:${dueTime}`, "YYYY-MM-DD:HH:mm").toISOString()
    : null;
  updatedData["deadline"] = deadlineStringToSend;

  const scheduleDate = _.get(newData, "state.scheduleDate", null);
  const scheduleTime = _.get(newData, "state.scheduleTime", null);
  const scheduleString = getScheduleString({
    scheduleDate,
    scheduleTime,
    assignmentLevelPreferences,
    oldScheduleDate: _.get(oldData, "state.scheduleDate", null),
    oldScheduleTime: _.get(oldData, "state.scheduleTime", null),
    isDueDateInPast,
  });
  const scheduleStringToSend = moment(
    scheduleString,
    "YYYY-MM-DD:HH:mm",
    true
  ).isValid()
    ? moment(scheduleString, "YYYY-MM-DD:HH:mm").toISOString()
    : moment(scheduleString, "YYYY-MM-DD").toISOString();
  updatedData["publishedAt"] = scheduleStringToSend;

  const newCloseDate = isFixedStartTime
    ? moment(scheduleStringToSend)
        .add(timedTestDuration.hours, "hours")
        .add(timedTestDuration.minutes, "minutes")
    : _.get(newData, "closeSubmissionDate", null);

  updatedData["closeSubmissionDate"] =
    !_.isEmpty(newCloseDate) &&
    _.includes(assignmentLevelPreferences, CLOSES_ON)
      ? moment(newCloseDate, "YYYY-MM-DD:HH:mm").toISOString()
      : null;
  updatedData["visibility"] = _.includes(assignmentLevelPreferences, VISIBILITY)
    ? _.get(newData, "visibility", VISIBLE)
    : VISIBLE;
  updatedData["deadline"] = isFixedStartTime
    ? moment(scheduleStringToSend)
        .add(timedTestDuration.hours, "hours")
        .add(timedTestDuration.minutes, "minutes")
        .toISOString()
    : !_.isEmpty(deadlineString) &&
      _.includes(assignmentLevelPreferences, DUE_ON)
    ? deadlineStringToSend
    : null;

  // When WorksheetAssignModalV2 is enabled, include per-class security mode
  // and duration in updated course data instead of sending as global settings
  if (getIsWorksheetAssignModalV2Enabled()) {
    updatedData["isLockScreenEnabled"] = _.get(
      newData,
      "isLockScreenEnabled",
      false
    );
    updatedData["isSafeBrowserEnabled"] = _.get(
      newData,
      "isSafeBrowserEnabled",
      false
    );
    const courseTimedTestDuration = _.get(newData, "timedTestDuration", null);
    if (courseTimedTestDuration) {
      updatedData["timedTestDuration"] =
        courseTimedTestDuration.hours * 60 + courseTimedTestDuration.minutes;
    }
  }

  return updatedData;
};

export const getEditAssignmentMutationCoursesData = params => {
  const {
    selectedState,
    assignmentData,
    genericFoldersLocal,
    dueTimePreference,
    backDateCourses,
  } = params;

  // On edit assignment when draft, we allow all courses data which were selected before to be updated
  const courses = {};
  const oldCourses = _.get(assignmentData, "courses", []);
  const newCourses = _.get(selectedState, "courses", []);

  // For fixed start time
  const isFixedStartTime = _.get(selectedState, "isFixedStartTime", false);
  const timedTestDuration = _.get(selectedState, "timedTestDuration", {});

  const metadata = _.get(selectedState, "metadata", {});

  const updatedCourses = [];
  _.forEach(newCourses, newCourseData => {
    const { id } = newCourseData;
    const oldCourseData = _.find(oldCourses, { id }) || {};
    if (!_.isEmpty(oldCourseData)) {
      const isBackdateAssignment = _.get(
        _.find(backDateCourses, { id }),
        "isBackdateAssignment",
        false
      );

      updatedCourses.push({
        courseId: id,
        ...getUpdatedCourseData({
          oldData: oldCourseData,
          newData: newCourseData,
          genericFoldersLocal,
          metadata,
          dueTimePreference,
          isFixedStartTime,
          timedTestDuration,
        }),
        isBackdateAssignment,
      });
    }
    if (!_.isEmpty(updatedCourses)) {
      courses["updatedCourses"] = updatedCourses;
    }
  });

  const addedCourses = _.differenceBy(newCourses, oldCourses, "id");
  if (!_.isEmpty(addedCourses)) {
    const { courses: addedCoursesData } = getAddedCoursesData({
      addedCourses,
      dueTimePreference,
      isFixedStartTime,
      timedTestDuration,
      metadata,
      backDateCourses,
    });
    courses["addedCourses"] = _.map(addedCoursesData, course =>
      _.omit(course, "title")
    );
  }

  const removedCourses = _.differenceBy(oldCourses, newCourses, "id");
  if (!_.isEmpty(removedCourses)) {
    courses["removedCourseIds"] = _.map(removedCourses, ({ id }) => id);
  }

  return { courses };
};

export const getTargetStep = params => {
  const {
    value,
    steps,
    currentStep,
    isValid,
    isWorksheetValid,
    assessmentDetailsRef,
  } = params;

  // assessmentDetailsRef will be empty if user has not visited assessment details step
  // in that case isValid and isWorksheetValid will always return true
  // so move to next step is steps are more than 1, else return the current step value
  if (_.isEmpty(assessmentDetailsRef)) {
    if (_.size(steps) > 1) {
      return steps.next({ current: currentStep, findKey: "value" }).value;
    } else {
      return _.get(_.first(steps), "value") || value;
    }
  }

  let valueToReturn = value; // here value is the actual step value which user selected

  _.forEach(steps, ({ value }) => {
    switch (value) {
      case ASSESSMENT_DETAILS:
      case SMT_CREATION:
      case FMT_CREATION:
      case QT_CREATION:
      case LE_CREATION:
      case ASSESSMENT_CREATION:
      case WORKSHEET_QUESTION_CREATION:
      case AI_TUTOR_CREATION:
        if (!isValid) valueToReturn = value;
        break;
      case QUESTION_CREATION:
        if (!isWorksheetValid) valueToReturn = value;
        break;
    }
  });

  return valueToReturn;
};

export const getIsNavigatingToPreviousStep = params => {
  const { steps, currentStep, value } = params;
  const prevStep = steps?.previous({ current: currentStep, findKey: "value" });
  return _.get(prevStep, "value") === value;
};

/**
 * Identifies courses with backdated assignments and categorizes backdate severity.
 *
 * Returns courses where the open date or both open and due date is in the past. For courses where both
 * open and due dates are backdated, adds an isBackdateAssignment flag.
 *
 * Aslo due date can not be of back date before open date.
 *
 * @param {Object} params - Function parameters
 * @param {Array} params.courses - Courses to check for backdating
 * @param {Array} params.oldCourses - Previous course data for comparison
 * @param {Array} params.assignmentLevelPreferences - Timeline settings
 * @param {boolean} params.isBackdateAssignment - Whether assignment was already backdated
 *
 * @returns {Object} Contains:
 *   - backDateCourses: Array of backdated courses (some with isBackdateAssignment: true)
 *   - hasBothOpenAndDueDateBackdated: Boolean indicating if any course has both dates backdated
 */
export const getBackDateAssignmentCoursesV2 = ({
  courses,
  oldCourses = [],
  assignmentLevelPreferences = [],
  isBackdateAssignment = false,
}) => {
  let hasBothOpenAndDueDateBackdated = false;

  const backDateCourses = _.reduce(
    courses,
    (result, course) => {
      const {
        state: { scheduleDate, scheduleTime } = {},
        deadlineDate,
        deadlineTime,
      } = course;

      // Get old course data for comparison
      const oldCourseData = _.find(oldCourses, { id: course.id }) || {};
      const {
        state: {
          scheduleDate: oldScheduleDate,
          scheduleTime: oldScheduleTime,
          state = "",
        } = {},
        deadlineDate: oldDeadlineDate,
        deadlineTime: oldDeadlineTime,
      } = oldCourseData;

      const isDraftMode = state === "DRAFT";

      // Check if open date is backdated
      const isOpenDateBackdated = checkOpenDateBackdated({
        scheduleDate,
        scheduleTime,
        oldScheduleDate,
        oldScheduleTime,
        assignmentLevelPreferences,
        deadlineDate,
        deadlineTime,
      });

      // Skip if open date is not backdated
      if (!isOpenDateBackdated) {
        return result;
      }

      // Check if both dates are backdated
      const isDueDateBackdated = checkDueDateBackdated({
        course,
        deadlineDate,
        deadlineTime,
        oldDeadlineDate,
        oldDeadlineTime,
        isBackdateAssignment,
        assignmentLevelPreferences,
        isDraftMode,
      });

      // Handle edge case: If due date not backdated and backdate modal for open date was already shown previously then skip
      const isOpenDateAlreadyBackDated = shouldSkipDueToOpenDateBackDated({
        isDraftMode,
        oldScheduleDate,
        oldScheduleTime,
      });
      if (!isDueDateBackdated && isOpenDateAlreadyBackDated) {
        return result;
      }

      // Add course to result with appropriate flag
      if (isDueDateBackdated) {
        hasBothOpenAndDueDateBackdated = true;
        result.push({ ...course, isBackdateAssignment: true });
      } else {
        result.push(course);
      }

      return result;
    },
    []
  );

  return {
    backDateCourses,
    hasBothOpenAndDueDateBackdated,
  };
};

/**
 * Checks if the open date is backdated
 */
const checkOpenDateBackdated = ({
  scheduleDate,
  scheduleTime,
  oldScheduleDate,
  oldScheduleTime,
  assignmentLevelPreferences,
  deadlineDate,
  deadlineTime,
}) => {
  const previousOpensOn = getMomentFromDateAndTimeString({
    dateString: oldScheduleDate,
    timeString: oldScheduleTime,
  });

  const opensOn = getMomentFromDateAndTimeString({
    dateString: scheduleDate,
    timeString: scheduleTime,
  });

  const dueOn = getMomentFromDateAndTimeString({
    dateString: deadlineDate,
    timeString: deadlineTime,
  });
  const dueOnToConsider = getDueOnBasedOnTimelineSettings({
    dueOn,
    currentTimelineSettings: assignmentLevelPreferences,
  });
  const isDueDateInPast = checkIsDateBefore({
    date1: dueOnToConsider,
    date2: moment(),
    key: "minutes",
  });

  const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
    opensOn,
    previousOpensOn,
    currentTimelineSettings: assignmentLevelPreferences,
    isDueDateInPast,
  });

  if (_.isEmpty(opensOnToConsider)) {
    return false;
  }

  return checkIsDateBefore({
    date1: opensOnToConsider,
    date2: moment(),
    key: "minutes",
  });
};

/**
 * Checks if both open and due dates are backdated
 */
const checkDueDateBackdated = ({
  deadlineDate,
  deadlineTime,
  assignmentLevelPreferences,
  isBackdateAssignment,
  isDraftMode = false,
  oldDeadlineDate,
  oldDeadlineTime,
}) => {
  if (_.isEmpty(deadlineDate)) {
    return false;
  }

  const dueOn = getMomentFromDateAndTimeString({
    dateString: deadlineDate,
    timeString: deadlineTime,
  });

  const dueOnToConsider = getDueOnBasedOnTimelineSettings({
    dueOn,
    currentTimelineSettings: assignmentLevelPreferences,
  });

  if (_.isEmpty(dueOnToConsider)) {
    return false;
  }

  const previousDueOn = getMomentFromDateAndTimeString({
    dateString: oldDeadlineDate,
    timeString: oldDeadlineTime,
  });

  // If assignment was not backdated previously, but naturally the previous due date passed we will not consider it as back date
  if (
    !isBackdateAssignment &&
    !_.isEmpty(previousDueOn) &&
    checkIsDateBefore({
      date1: previousDueOn,
      date2: moment(),
      key: "minutes",
    }) &&
    !isDraftMode
  ) {
    return false;
  }

  const isDueDateBackdated = checkIsDateBefore({
    date1: dueOnToConsider,
    date2: moment(),
    key: "minutes",
  });

  return isDueDateBackdated;
};

/**
 * Checks if course should be skipped due to same schedule dates
 */
const shouldSkipDueToOpenDateBackDated = ({
  oldScheduleDate,
  oldScheduleTime,
  isDraftMode,
}) => {
  if (_.isEmpty(oldScheduleDate) || isDraftMode) {
    return false;
  }
  // If open date is already of past and it is not the case of due date back date then dont need to show the modal again
  const previousOpensOn = getMomentFromDateAndTimeString({
    dateString: oldScheduleDate,
    timeString: oldScheduleTime,
  });

  return checkIsDateBefore({
    date1: previousOpensOn,
    date2: moment(),
    key: "minutes",
  });
};

export const getScheduleString = ({
  scheduleDate,
  scheduleTime,
  assignmentLevelPreferences = [],
  oldScheduleDate,
  oldScheduleTime,
  isDueDateInPast = false,
}) => {
  if (_.isEmpty(scheduleDate)) {
    return generateDateTimeString({
      dateString: moment().format("YYYY-MM-DD"),
      timeString: moment().format("HH:mm"),
    });
  }

  const newScheduleString = generateDateTimeString({
    dateString: scheduleDate,
    timeString: scheduleTime,
  });

  const isScheduleDateUpdated =
    _.isEmpty(oldScheduleDate) ||
    !checkIsDateSame({
      date1: moment(newScheduleString),
      date2: moment(
        generateDateTimeString({
          dateString: oldScheduleDate,
          timeString: oldScheduleTime,
        })
      ),
      check: "minutes",
    });

  if (
    !_.includes(assignmentLevelPreferences, OPENS_ON) &&
    isScheduleDateUpdated &&
    !isDueDateInPast
  ) {
    return generateDateTimeString({
      dateString: moment().format("YYYY-MM-DD"),
      timeString: moment().format("HH:mm"),
    });
  }

  return newScheduleString;
};

export const getAdvancedTimelineSettingsOptions = (t = () => {}) => {
  return [
    {
      key: OPENS_ON,
      label: t("common:opens_on"),
      subText: t("classRoom:task_opens_on_set_date_subtext"),
      testId: "configureAssignSettings-opensOn",
    },
    {
      key: DUE_ON,
      label: t("classRoom:due_on"),
      subText: t("classRoom:submission_due_on_subtext"),
      testId: "configureAssignSettings-dueOn",
    },
    {
      key: CLOSES_ON,
      label: t("common:closes_on"),
      subText: t("classRoom:submission_closes_after_set_date_subtext"),
      showNewTag: true,
      testId: "configureAssignSettings-closesOn",
    },
    {
      key: VISIBILITY,
      label: t("common:visibility"),
      subText: t("classRoom:visibility_subtext"),
      showNewTag: true,
      testId: "configureAssignSettings-visibility",
    },
  ];
};

export const getShowUpdateAITutorModal = async params => {
  const { assignmentId } = params;

  const { assignedStudents } = await getAssignedStudentsAIConversation({
    id: assignmentId,
  });

  return _.some(assignedStudents, assignedStudent => {
    const { isSubmitted, aiConversation } = assignedStudent || {};
    return !isSubmitted && _.get(aiConversation, "messages.totalCount") > 1;
  });
};
